import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { FormikProvider, getIn, useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";

import messages from "../../../../../assets/locale/messages";
import useRevalidateOnLocaleChange from "../../../../../hooks/useRevalidateOnLocaleChange";
import { showErrorMsg, showWarningMsg } from "../../../../../utils/Helpers";
import { getCompanyClientsRequest } from "../../../../../store/CompanyPortal/Clients/actions";
import * as adminCompanyClientsApi from "../../../../../network/apis/adminCompanyClients";
import * as lookupsApi from "../../../../../network/apis/lookups";
import { getCompanyPortalCities } from "../../../../../network/apis/companyPortalEventRequests";
import { normalizeRoleLookupOptions } from "../../../Event/Applicants/eventRequestMappers";
import { getLatLngFromAddress } from "../../../../../utils/Geocode";
import EventIdentitySection from "./EventIdentitySection";
import EventTimelineSection from "./EventTimelineSection";
import LocationBoundaries from "./LocationBoundaries/LocationBoundaries";
import ManagementClientSection from "./ManagementClientSection";
import getBasicInformationValidationSchema from "./validationSchema";
import getPortalBasicInformationValidationSchema from "./portalValidationSchema";
import {
  buildPortalBasicInfoRequestBody,
  getPortalApiErrorMessage,
  mapCompanyClientRowToSelectOption,
  mapCompanyUserToPmOption,
  mapPortalEventToBasicInfoValues,
} from "../../../../../utils/companyPortal/eventApi";
import { getAdminCompanyUsers } from "../../../../../network/apis/adminCompanyUsers";
import { getCompanyUsers } from "../../../../../network/apis/companyPortalEmployees";
import {
  isPortalEventOngoing,
  isPortalEventOngoingOrUpcoming,
} from "../../../../../utils/companyPortal/eventEditRules";
import {
  countFormErrors,
  focusFirstError,
  getFirstErrorPath,
} from "../validationFocus";
import { DAILY_EVENT_MAX_DAYS } from "../ChooseCrewSize/crewSizeUtils";
import { applicantsProfileStatus, eventStatuses } from "../../../../../utils/Constants";

import "./BasicInformation.scss";

const verifiedCompanyUsersParams = {
  page: -1,
  profile_status: applicantsProfileStatus.verified,
};

/** Keys aligned with Company Portal / Rails event attributes where practical. */
const initialValues = {
  image_url: "",
  name: "",
  event_type_id: null,
  address: "",
  description: "",
  payment_period: "daily",
  is_single_day: true,
  number_of_days:null,
  paid_days_off: null,
  location: {
    address: "",
    lat: "",
    lng: "",
    radius: "",
    city_id: null,
  },
  building_start_at: "",
  building_end_at: "",
  event_start_at: "",
  event_end_at: "",
  dismantling_start_at: "",
  dismantling_end_at: "",
  attendance_verification_method: "basic_verification",
  projectManagers: [],
  destroyedProjectManagers: [],
  client: {
    id: null,
    admin_id: null,
  },
};

const getVisibleError = (formik, name) => {
  const error = getIn(formik.errors, name);
  const touched = getIn(formik.touched, name);
  return touched || formik.submitCount > 0 ? error : "";
};

/** Formik/Yup may set `errors.location` to a string (schema test) or an object (nested field errors). */
const firstStringError = (value) => {
  if (value == null || value === "") return "";
  if (typeof value === "string") return value;
  if (typeof value === "object" && !Array.isArray(value)) {
    for (const nested of Object.values(value)) {
      const s = firstStringError(nested);
      if (s) return s;
    }
  }
  return "";
};

const getDurationInDays = (start, end) => {
  if (!start || !end) return 0;
  const diff = new Date(end).getTime() - new Date(start).getTime();
  return diff > 0 ? diff / (1000 * 60 * 60 * 24) : 0;
};

const hasValueChanged = (current, initial) =>
  String(current ?? "") !== String(initial ?? "");

const hasErrorIn = (errors, paths) =>
  paths.some((path) => Boolean(firstStringError(getIn(errors, path))));

/** @param {unknown} value */
const isBrowserFile = (value) =>
  typeof File !== "undefined" && value instanceof File;

const BasicInformation = ({
  isEditMode,
  registerSubmit,
  registerStepApi,
  companyPortalMode,
  requestsPortal,
  eventTypeOptions,
  initialEvent,
  onEventLocationBoundarySync,
  eventCompanyId,
}) => {
  const dispatch = useDispatch();
  const lang = useSelector((state) => state.locale.lang);
  const { shared } = messages[lang] ?? messages.en;
  const companyClientsList = useSelector((state) => state.companyClients.list);
  const isCompanyRequestsPortal = requestsPortal === "company";
  const [adminCompanyClients, setAdminCompanyClients] = useState([]);
  const labels =
    messages[lang]?.events?.creatEvent?.basicInformation ||
    messages.en.events.creatEvent.basicInformation;

  /** Covers S3 upload inside buildPayload before parent draft/next loading kicks in. */
  const [portalLogoSubmitLoading, setPortalLogoSubmitLoading] = useState(false);
  const [portalLogoUploadPercent, setPortalLogoUploadPercent] =
    useState(/** @type {number | null} */ (null));
  const [validationAttempt, setValidationAttempt] = useState(0);
  const [managerOptions, setManagerOptions] = useState([]);
  const [managerOptionsLoadFailed, setManagerOptionsLoadFailed] =
    useState(false);
  const [cityOptions, setCityOptions] = useState([]);
  const [citySuggestedMapCenter, setCitySuggestedMapCenter] = useState(null);
  const geocodedCityIdRef = useRef(null);
  const cityGeocodeRequestIdRef = useRef(0);
  /** Set by EventIdentitySection; lets Save/Continue await a pending debounced geocode. */
  const geocodeFlushRef = useRef(null);

  const validationSchema = useMemo(
    () =>
      companyPortalMode
        ? getPortalBasicInformationValidationSchema(labels.errors)
        : getBasicInformationValidationSchema(labels.errors),
    [labels, companyPortalMode],
  );

  const showBuildUpAndDismantling = useMemo(
    () => !companyPortalMode,
    [companyPortalMode],
  );

  useEffect(() => {
    if (isCompanyRequestsPortal) {
      dispatch(getCompanyClientsRequest({ page: -1 }));
      return undefined;
    }

    if (!eventCompanyId) {
      setAdminCompanyClients([]);
      return undefined;
    }

    let cancelled = false;

    adminCompanyClientsApi
      .getAdminCompanyClients({ company_id: eventCompanyId, page: -1 })
      .then((response) => {
        if (cancelled) return;
        const rows = Array.isArray(response?.data?.data) ? response.data.data : [];
        setAdminCompanyClients(rows);
      })
      .catch((error) => {
        if (cancelled) return;
        setAdminCompanyClients([]);
        showErrorMsg(getPortalApiErrorMessage(error, shared.generalErrMsg));
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, eventCompanyId, isCompanyRequestsPortal, shared.generalErrMsg]);

  useEffect(() => {
    let cancelled = false;

    const citiesRequest = isCompanyRequestsPortal
      ? getCompanyPortalCities({ page: -1 })
      : lookupsApi.getCities({ page: -1 });

    citiesRequest
      .then((response) => {
        if (cancelled) return;
        setCityOptions(normalizeRoleLookupOptions(response.data?.data, lang));
      })
      .catch(() => {
        if (!cancelled) setCityOptions([]);
      });

    return () => {
      cancelled = true;
    };
  }, [isCompanyRequestsPortal, lang]);

  const clientOptions = useMemo(() => {
    const list = isCompanyRequestsPortal ? companyClientsList : adminCompanyClients;
    return (list || [])
      .map(mapCompanyClientRowToSelectOption)
      .filter((option) => option.id != null && option.id !== "");
  }, [adminCompanyClients, companyClientsList, isCompanyRequestsPortal]);

  const portalOngoingCompanyEdit = useMemo(
    () =>
      Boolean(
        companyPortalMode &&
          isEditMode &&
          initialEvent?.status &&
          isPortalEventOngoing(initialEvent.status),
      ),
    [companyPortalMode, isEditMode, initialEvent?.status],
  );

  const formInitialValues = useMemo(
    () =>
      initialEvent
        ? mapPortalEventToBasicInfoValues(initialEvent)
        : initialValues,
    [initialEvent],
  );

  const formik = useFormik({
    initialValues: formInitialValues,
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: () => {},
  });

  // Published: only the Super Admin changes the dates (PROLINE-3932), which also covers
  // the lock 2 hours before the start.
  const portalTimelinePublishedLocked =
    companyPortalMode &&
    isEditMode &&
    isPortalEventOngoingOrUpcoming(initialEvent?.status);
  // Pending (publishing request with the Super Admin): the event dates are locked.
  const portalTimelinePendingLocked =
    companyPortalMode && initialEvent?.status === eventStatuses.pendingPayment;

  const companyId = useMemo(() => {
    const fromEvent =
      initialEvent?.company_id ?? initialEvent?.company?.id ?? null;
    if (fromEvent != null && fromEvent !== "") return fromEvent;
    if (typeof window === "undefined") return null;
    try {
      return (
        window.sessionStorage.getItem("adminCreateEventCompanyId") ||
        new URLSearchParams(window.location.search).get("company_id")
      );
    } catch (_e) {
      return null;
    }
  }, [initialEvent]);

  const managerOptionsHint = useMemo(() => {
    if (requestsPortal === "admin" && !companyId) {
      return labels.projectManagersCompanyRequired;
    }
    if (managerOptionsLoadFailed) {
      return labels.projectManagersLoadError;
    }
    return "";
  }, [
    requestsPortal,
    companyId,
    managerOptionsLoadFailed,
    labels.projectManagersCompanyRequired,
    labels.projectManagersLoadError,
  ]);

  useEffect(() => {
    let cancelled = false;

    const loadManagers = async () => {
      if (!cancelled) {
        setManagerOptionsLoadFailed(false);
      }

      try {
        if (requestsPortal === "company") {
          const res = await getCompanyUsers(verifiedCompanyUsersParams);
          const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
          if (!cancelled) {
            setManagerOptions(rows.map(mapCompanyUserToPmOption));
          }
          return;
        }

        if (!companyId) {
          if (!cancelled) setManagerOptions([]);
          return;
        }

        const res = await getAdminCompanyUsers({
          ...verifiedCompanyUsersParams,
          company_id: companyId,
        });
        const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
        if (!cancelled) {
          setManagerOptions(rows.map(mapCompanyUserToPmOption));
        }
      } catch (e) {
        if (!cancelled) {
          setManagerOptions([]);
          setManagerOptionsLoadFailed(true);
          showErrorMsg(
            e?.response?.data?.message || labels.projectManagersLoadError,
          );
        }
      }
    };

    void loadManagers();

    return () => {
      cancelled = true;
    };
  }, [
    requestsPortal,
    companyId,
    labels.projectManagersLoadError,
  ]);

  const restoredEventIdRef = useRef(null);
  /** Snackbar for auto Daily→Monthly: show once until duration drops back to ≤5 days. */
  const autoMonthlyConversionSnackShownRef = useRef(false);

  useEffect(() => {
    const restoredEventId = initialEvent?.id;
    if (!restoredEventId || restoredEventIdRef.current === restoredEventId) {
      return;
    }

    restoredEventIdRef.current = restoredEventId;
    formik.setValues(mapPortalEventToBasicInfoValues(initialEvent), false);
    formik.setFieldValue("destroyedProjectManagers", [], false);
    formik.setTouched({}, false);
    setValidationAttempt(0);
    autoMonthlyConversionSnackShownRef.current = false;
  }, [initialEvent]);

  useRevalidateOnLocaleChange(() => {
    void formik.validateForm();
  });

  useEffect(() => {
    const userChangedEventTimeline =
      !isEditMode ||
      !initialEvent ||
      formik.touched.event_start_at ||
      formik.touched.event_end_at ||
      hasValueChanged(
        formik.values.event_start_at,
        formInitialValues.event_start_at,
      ) ||
      hasValueChanged(
        formik.values.event_end_at,
        formInitialValues.event_end_at,
      );

    if (!userChangedEventTimeline) {
      return;
    }

    const duration = getDurationInDays(
      formik.values.event_start_at,
      formik.values.event_end_at,
    );

    if (duration <= DAILY_EVENT_MAX_DAYS) {
      autoMonthlyConversionSnackShownRef.current = false;
      return;
    }

    if (formik.values.payment_period !== "daily") {
      return;
    }

    formik.setFieldValue("payment_period", "monthly", false);
    if (!autoMonthlyConversionSnackShownRef.current) {
      showWarningMsg(labels.recurrenceAutoMonthlyHint);
      autoMonthlyConversionSnackShownRef.current = true;
    }
  }, [
    formik.values.event_start_at,
    formik.values.event_end_at,
    formik.values.payment_period,
    formik.touched.event_start_at,
    formik.touched.event_end_at,
    formInitialValues.event_start_at,
    formInitialValues.event_end_at,
    initialEvent,
    isEditMode,
    lang,
    labels.recurrenceAutoMonthlyHint,
  ]);

  const canSelectPaymentPeriod = useCallback(
    (next) => {
      if (next !== "daily") return true;
      const duration = getDurationInDays(
        formik.values.event_start_at,
        formik.values.event_end_at,
      );
      if (duration > DAILY_EVENT_MAX_DAYS) {
        showWarningMsg(labels.recurrenceDailyBlockedLongEvent);
        return false;
      }
      return true;
    },
    [
      formik.values.event_start_at,
      formik.values.event_end_at,
      labels.recurrenceDailyBlockedLongEvent,
    ],
  );

  useEffect(() => {
    if (formik.values.payment_period === "monthly" && formik.values.isSingleDay) {
      formik.setFieldValue("isSingleDay", false, false);
    }
  }, [formik.values.payment_period, formik.values.isSingleDay]);

  useEffect(() => {
    if (formik.values.payment_period !== "monthly") {
      if (formik.values.number_of_days !== "") {
        formik.setFieldValue("number_of_days", "", false);
      }
      if (formik.values.paid_days_off !== "") {
        formik.setFieldValue("paid_days_off", "", false);
      }
    }
  }, [formik.values.payment_period, formik.values.number_of_days, formik.values.paid_days_off]);

  /**
   * Updates `location` and the top-level `address` together in one atomic, validated
   * step. Setting them via two separate setFieldValue calls (the second skipping
   * validation) left a stale "Location name is required" error on `address` after
   * picking a location on the map, even once the field held a valid value again.
   */
  const handleLocationChange = (nextLocation) => {
    formik.setValues(
      {
        ...formik.values,
        location: nextLocation,
        address: nextLocation?.address || formik.values.address,
      },
      true,
    );
    formik.setFieldTouched("location.address", true, false);
    formik.setFieldTouched("location.lat", true, false);
    formik.setFieldTouched("location.lng", true, false);
    formik.setFieldTouched("location.radius", true, false);
  };

  /**
   * When a city is picked before any location is chosen, recenter the (empty)
   * map on that city as a starting point — purely visual, doesn't touch
   * `location.address/lat/lng`, so the field stays required as before.
   */
  useEffect(() => {
    const cityId = formik.values.location?.city_id;
    const hasLocation = Boolean(
      formik.values.location?.lat && formik.values.location?.lng,
    );

    if (cityId == null || cityId === "" || hasLocation) {
      setCitySuggestedMapCenter(null);
      return;
    }

    if (String(geocodedCityIdRef.current) === String(cityId)) return;

    const cityName = cityOptions.find(
      (option) => String(option.id) === String(cityId),
    )?.name;
    if (!cityName) return;

    geocodedCityIdRef.current = cityId;
    const requestId = ++cityGeocodeRequestIdRef.current;
    getLatLngFromAddress(
      cityName,
      (lat, lng) => {
        if (requestId !== cityGeocodeRequestIdRef.current) return;
        setCitySuggestedMapCenter({ lat, lng });
      },
      () => {
        if (requestId !== cityGeocodeRequestIdRef.current) return;
        setCitySuggestedMapCenter(null);
      },
    );
  }, [
    formik.values.location?.city_id,
    formik.values.location?.lat,
    formik.values.location?.lng,
    cityOptions,
  ]);

  useEffect(() => {
    const radius = formik.values.location?.radius;
    onEventLocationBoundarySync?.(
      radius == null || radius === "" ? "" : String(radius),
    );
  }, [formik.values.location?.radius, onEventLocationBoundarySync]);

  const buildTouchedForValidation = () =>
    companyPortalMode
      ? {
          image_url: true,
          name: true,
          event_type_id: true,
          address: true,
          description: true,
          payment_period: true,
          attendance_verification_method: true,
          location: {
            address: true,
            lat: true,
            lng: true,
            radius: true,
          },
          location_city_id: true,
          event_start_at: true,
          event_end_at: true,
          ...(showBuildUpAndDismantling
            ? {
                building_start_at: true,
                building_end_at: true,
                dismantling_start_at: true,
                dismantling_end_at: true,
              }
            : {}),
          number_of_days: formik.values.payment_period === "monthly",
          paid_days_off: formik.values.payment_period === "monthly",
        }
      : {
          image_url: true,
          name: true,
          event_type_id: true,
          address: true,
          description: true,
          location: {
            address: true,
            lat: true,
            lng: true,
            radius: true,
          },
          location_city_id: true,
          event_start_at: true,
          event_end_at: true,
          ...(showBuildUpAndDismantling
            ? {
                building_start_at: true,
                building_end_at: true,
                dismantling_start_at: true,
                dismantling_end_at: true,
              }
            : {}),
          attendance_verification_method: true,
          number_of_days: formik.values.payment_period === "monthly",
          paid_days_off: formik.values.payment_period === "monthly",
        };

  const validateContinue = async () => {
    await geocodeFlushRef.current?.();
    const errors = await formik.validateForm();
    formik.setTouched(buildTouchedForValidation(), false);
    const isValid = Object.keys(errors).length === 0;
    if (!isValid) {
      setValidationAttempt((current) => current + 1);
      focusFirstError(getFirstErrorPath(errors), {
        aliases: {
          "location.address": "address",
          "location.lat": "address",
          "location.lng": "address",
          "location.radius": "eventRadius",
          "location_city_id": "city_id",
        },
        sectionSelector: ".form-section--error, .card-container",
      });
    }
    return isValid;
  };

  const validatePortalDraft = async () => {
    await geocodeFlushRef.current?.();
    const errors = await formik.validateForm();
    formik.setTouched(buildTouchedForValidation(), false);
    const isValid = Object.keys(errors).length === 0;
    if (!isValid) {
      setValidationAttempt((current) => current + 1);
      focusFirstError(getFirstErrorPath(errors), {
        aliases: {
          "location.address": "address",
          "location.lat": "address",
          "location.lng": "address",
          "location.radius": "eventRadius",
          "location_city_id": "city_id",
        },
        sectionSelector: ".form-section--error, .card-container",
      });
    }
    return isValid;
  };

  const showSectionErrors = validationAttempt > 0;
  const identityHasError =
    showSectionErrors &&
    hasErrorIn(formik.errors, [
      "image_url",
      "name",
      "event_type_id",
      "address",
      "location_city_id",
      "description",
      "payment_period",
      "number_of_days",
      "paid_days_off",
    ]);
  const timelineHasError =
    showSectionErrors &&
    hasErrorIn(
      formik.errors,
      showBuildUpAndDismantling
        ? [
            "building_start_at",
            "building_end_at",
            "event_start_at",
            "event_end_at",
            "dismantling_start_at",
            "dismantling_end_at",
          ]
        : ["event_start_at", "event_end_at"],
    );
  const locationHasError =
    showSectionErrors &&
    hasErrorIn(formik.errors, [
      "location",
      "location.address",
      "location.lat",
      "location.lng",
      "location.radius",
    ]);
  const managementHasError =
    showSectionErrors &&
    hasErrorIn(formik.errors, [
      "attendance_verification_method",
      "projectManagers",
      "client",
      "client.admin_id",
    ]);

  useEffect(() => {
    if (registerStepApi) {
      registerStepApi({
        validateContinue,
        validatePortalDraft,
        // Silent check for Submit for Approval: one item when the step isn't filled in
        // (e.g. a draft step never completed), not one per empty field.
        countMissingFields: async () =>
          countFormErrors(await formik.validateForm()) > 0 ? 1 : 0,
        getSubmitIssues: () => [
          (messages[lang] ?? messages.en).events.creatEvent.submissionFailed
            .completeStep,
        ],
        buildPayload: async () => {
          const hadNewLocalFile = isBrowserFile(formik.values.image_url);
          if (hadNewLocalFile) {
            setPortalLogoSubmitLoading(true);
            setPortalLogoUploadPercent(null);
          }
          try {
            const body = await buildPortalBasicInfoRequestBody(formik.values, {
              onUploadProgress: hadNewLocalFile
                ? (pct) => setPortalLogoUploadPercent(pct)
                : undefined,
            });
            if (body.image_url === null) {
              formik.setFieldValue("image_url", "", false);
            } else if (hadNewLocalFile && body.image_url) {
              formik.setFieldValue("image_url", body.image_url, false);
            }
            return body;
          } finally {
            if (hadNewLocalFile) {
              setPortalLogoSubmitLoading(false);
              setPortalLogoUploadPercent(null);
            }
          }
        },
        buildSummary: () => ({
          name: String(formik.values.name || "").trim(),
          image_url:
            typeof formik.values.image_url === "string"
              ? formik.values.image_url
              : "",
        }),
      });
      return () => registerStepApi(null);
    }

    registerSubmit?.(async () => validateContinue());

    return () => registerSubmit?.(null);
  }, [formik, registerStepApi, registerSubmit, companyPortalMode]);

  return (
    <FormikProvider value={formik}>
      <form
        className="basic-information-step"
        dir={lang === "ar" ? "rtl" : "ltr"}
        onSubmit={formik.handleSubmit}
      >
        <div className="basic-information-step__intro">
          <h1>{labels.pageTitle}</h1>
          <p>{labels.pageSubtitle}</p>
        </div>

        <div className="basic-information-step__layout">
          <div className="basic-information-step__main">
            <EventIdentitySection
              formik={formik}
              labels={labels}
              eventTypeOptions={eventTypeOptions}
              cityOptions={cityOptions}
              cityError={firstStringError(
                getVisibleError(formik, "location_city_id"),
              )}
              logoOptional
              portalLogoSubmitLoading={companyPortalMode && portalLogoSubmitLoading}
              portalLogoUploadPercent={portalLogoUploadPercent ?? undefined}
              canSelectPaymentPeriod={canSelectPaymentPeriod}
              hasError={identityHasError}
              disableIdentityFields={portalOngoingCompanyEdit}
              disableRecurrenceFields={portalOngoingCompanyEdit || portalTimelinePendingLocked}
              disableAddressField={portalOngoingCompanyEdit}
              flushGeocodeRef={geocodeFlushRef}
            />
            <EventTimelineSection
              labels={labels}
              hasError={timelineHasError}
              showBuildUpAndDismantling={showBuildUpAndDismantling}
              timelineFieldsDisabled={portalTimelinePublishedLocked}
              actualEventDisabled={
                portalTimelinePublishedLocked || portalTimelinePendingLocked
              }
            />
            <ManagementClientSection
              formik={formik}
              labels={labels}
              hasError={managementHasError}
              managerOptions={managerOptions}
              managerOptionsHint={managerOptionsHint}
              clientOptions={clientOptions}
            />
          </div>

          <aside className="basic-information-step__aside">
            <LocationBoundaries
              value={formik.values.location}
              onChange={handleLocationChange}
              required
              locationError={firstStringError(
                getVisibleError(formik, "location") ||
                  getVisibleError(formik, "location.address") ||
                  getVisibleError(formik, "location.lat") ||
                  getVisibleError(formik, "location.lng"),
              )}
              radiusError={firstStringError(
                getVisibleError(formik, "location.radius"),
              )}
              hasError={locationHasError}
              labels={{
                title: labels.location,
                eventLocation: labels.eventLocation,
                emptyAddress: labels.emptyAddress,
                boundaries: labels.locationBoundaries,
                boundariesPlaceholder: labels.boundariesPlaceholder,
                radiusFromCenter: labels.radiusFromCenter,
              }}
              mapInteractionsDisabled={portalOngoingCompanyEdit}
              radiusFieldDisabled={false}
              suggestedCenter={citySuggestedMapCenter}
            />
          </aside>
        </div>

        {isEditMode && <input type="hidden" name="mode" value="edit" readOnly />}
      </form>
    </FormikProvider>
  );
};

export default BasicInformation;

BasicInformation.propTypes = {
  isEditMode: PropTypes.bool,
  registerSubmit: PropTypes.func,
  registerStepApi: PropTypes.func,
  companyPortalMode: PropTypes.bool,
  requestsPortal: PropTypes.oneOf(["admin", "company"]),
  eventTypeOptions: PropTypes.arrayOf(PropTypes.object),
  initialEvent: PropTypes.object,
  onEventLocationBoundarySync: PropTypes.func,
  eventCompanyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

BasicInformation.defaultProps = {
  isEditMode: false,
  registerSubmit: undefined,
  registerStepApi: undefined,
  companyPortalMode: false,
  requestsPortal: "company",
  eventTypeOptions: undefined,
  initialEvent: undefined,
  onEventLocationBoundarySync: undefined,
  eventCompanyId: undefined,
};
